class AllArticles extends HTMLElement {
  connectedCallback() {
    if (this.ownerDocument !== document) return;
    if (this.hasAttribute('data-aa-ready')) return;
    this.setAttribute('data-aa-ready', '');

    this.cacheDom();
    this.mode = 'blog';
    this.searchQuery = '';
    this.activeTag = this.dataset.activeTag || '';
    this.searchTimer = null;
    this.abortController = null;

    this.onSearchInput = this.onSearchInput.bind(this);
    this.onChipClick = this.onChipClick.bind(this);
    this.onLoadMore = this.onLoadMore.bind(this);
    this.onPopState = this.onPopState.bind(this);

    this.bind();
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    if (this.abortController) this.abortController.abort();
  }

  cacheDom() {
    this.searchInput = this.querySelector('[data-aa-search]');
    this.chips = this.querySelectorAll('[data-aa-chip]');
    this.countEl = this.querySelector('[data-aa-count]');
    this.feed = this.querySelector('[data-aa-feed]');
    this.topGrid = this.querySelector('[data-aa-top-grid]');
    this.bottomGrid = this.querySelector('[data-aa-bottom-grid]');
    this.banner = this.querySelector('[data-aa-banner]');
    this.empty = this.querySelector('[data-aa-empty]');
    this.loadWrap = this.querySelector('[data-aa-load-wrap]');
    this.loadButton = this.querySelector('[data-aa-load-more]');
  }

  bind() {
    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.onSearchInput);
    }

    this.chips.forEach((chip) => {
      chip.addEventListener('click', this.onChipClick);
    });

    if (this.loadButton) {
      this.loadButton.addEventListener('click', this.onLoadMore);
    }
  }

  onSearchInput(event) {
    const query = event.target.value.trim();
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => {
      this.searchQuery = query;
      if (query.length === 0) {
        this.mode = 'blog';
        this.fetchBlog(this.blogUrlForTag(this.activeTag), { push: false });
        return;
      }
      this.mode = 'search';
      this.fetchSearch(query, { append: false });
    }, 320);
  }

  onChipClick(event) {
    const chip = event.currentTarget;
    const url = chip.getAttribute('href');
    if (!url || chip.hasAttribute('aria-current')) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.activeTag = chip.dataset.tag || '';
    this.dataset.activeTag = this.activeTag;
    this.setActiveChip(this.activeTag);

    if (this.searchInput) this.searchInput.value = '';
    this.searchQuery = '';
    this.mode = 'blog';
    this.fetchBlog(url, { push: true });
  }

  onLoadMore(event) {
    event.preventDefault();
    const nextUrl =
      (this.loadButton && this.loadButton.getAttribute('data-next-url')) || '';
    if (!nextUrl || this.loadButton.disabled || this.loadButton.getAttribute('aria-disabled') === 'true') return;

    if (this.mode === 'search') {
      this.fetchSearch(this.searchQuery, { append: true, pageUrl: nextUrl });
      return;
    }

    this.fetchBlog(nextUrl, { append: true, push: false });
  }

  onPopState() {
    const url = window.location.pathname + window.location.search;
    this.activeTag = this.tagFromPath(window.location.pathname);
    this.dataset.activeTag = this.activeTag;
    this.setActiveChip(this.activeTag);
    if (this.searchInput) this.searchInput.value = '';
    this.searchQuery = '';
    this.mode = 'blog';
    this.fetchBlog(url, { push: false });
  }

  setActiveChip(tagHandle) {
    this.chips.forEach((chip) => {
      const isActive = (chip.dataset.tag || '') === (tagHandle || '');
      chip.classList.toggle('is-active', isActive);
      if (isActive) {
        chip.setAttribute('aria-current', 'page');
      } else {
        chip.removeAttribute('aria-current');
      }
    });
  }

  blogUrlForTag(tagHandle) {
    const base = this.dataset.blogUrl || window.location.pathname;
    if (!tagHandle) return base;
    return `${base.replace(/\/$/, '')}/tagged/${tagHandle}`;
  }

  tagFromPath(pathname) {
    const match = pathname.match(/\/tagged\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  withSectionId(url) {
    const next = new URL(url, window.location.origin);
    const current = new URL(window.location.href);
    next.searchParams.set('section_id', this.dataset.sectionId);
    ['view', 'preview_theme_id', 'key', 'preview_key'].forEach((param) => {
      if (!next.searchParams.get(param) && current.searchParams.get(param)) {
        next.searchParams.set(param, current.searchParams.get(param));
      }
    });
    return next.pathname + next.search;
  }

  stripSectionId(url) {
    const next = new URL(url, window.location.origin);
    next.searchParams.delete('section_id');
    return next.pathname + next.search;
  }

  startRequest() {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.setLoading(true);
    return this.abortController;
  }

  async fetchHtml(url, controller) {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return response.text();
  }

  finishLoading(controller) {
    if (this.abortController === controller) this.setLoading(false);
  }

  parseDocument(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    if (this.loadButton) {
      this.loadButton.disabled = isLoading;
      this.loadButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
      this.loadButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
    if (this.searchInput) this.searchInput.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  async fetchBlog(url, { append = false, push = false } = {}) {
    const controller = this.startRequest();
    try {
      const html = await this.fetchHtml(this.withSectionId(url), controller);
      const doc = this.parseDocument(html);
      const incoming = doc.querySelector('all-articles');
      if (!incoming) return;

      if (append) {
        this.appendIncomingCards(incoming);
        this.syncLoadMore(incoming);
      } else {
        this.replaceFeed(incoming);
        if (push) {
          history.pushState({ tag: this.activeTag }, '', this.stripSectionId(url));
        }
      }

      this.toggleEmpty();
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      this.finishLoading(controller);
    }
  }

  async fetchSearch(query, { append = false, pageUrl = null } = {}) {
    const searchRoot = this.dataset.searchUrl || '/search';
    const url = pageUrl
      ? this.ensureSearchView(pageUrl)
      : `${searchRoot}?q=${encodeURIComponent(query)}&type=article&view=all-articles`;

    const controller = this.startRequest();
    try {
      const html = await this.fetchHtml(url, controller);
      const doc = this.parseDocument(html);
      const payload = doc.querySelector('[data-aa-search-payload]');
      if (!payload) return;

      let cards = Array.from(payload.querySelectorAll('[data-aa-card]'));
      if (this.activeTag) {
        cards = cards.filter((card) => this.cardHasTag(card, this.activeTag));
      }

      if (append) {
        this.appendCards(cards);
      } else {
        this.renderCards(cards);
      }

      const nextUrl = payload.dataset.nextUrl || '';
      this.updateLoadMore(nextUrl);
      this.updateCount(this.visibleCards().length, payload.dataset.count);
      this.toggleEmpty();
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      this.finishLoading(controller);
    }
  }

  ensureSearchView(url) {
    const next = new URL(url, window.location.origin);
    next.searchParams.set('view', 'all-articles');
    next.searchParams.set('type', 'article');
    return next.pathname + next.search;
  }

  cardHasTag(card, tagHandle) {
    const tags = (card.dataset.tags || '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
    return tags.includes(tagHandle);
  }

  appendIncomingCards(incoming) {
    this.appendCards(incoming.querySelectorAll('[data-aa-card]'));
  }

  appendCards(cards) {
    if (!this.bottomGrid) this.cacheDom();
    const target = this.bottomGrid || this.topGrid;
    if (!target) return;

    cards.forEach((card) => {
      target.appendChild(document.importNode(card, true));
    });
  }

  replaceFeed(incoming) {
    if (incoming.dataset.totalCount) {
      this.dataset.totalCount = incoming.dataset.totalCount;
    }
    const nextFeed = incoming.querySelector('[data-aa-feed]');
    const nextCount = incoming.querySelector('[data-aa-count]');
    if (nextFeed && this.feed) {
      this.feed.replaceWith(document.importNode(nextFeed, true));
    }
    if (nextCount && this.countEl) {
      this.countEl.replaceWith(document.importNode(nextCount, true));
    }
    this.cacheDom();
    if (this.loadButton) {
      this.loadButton.addEventListener('click', this.onLoadMore);
    }
  }

  renderCards(cards) {
    const breakAt = Number(this.dataset.bannerAfter || 6);
    if (this.topGrid) this.topGrid.replaceChildren();
    if (this.bottomGrid) this.bottomGrid.replaceChildren();

    cards.forEach((card, index) => {
      if (index < breakAt && this.topGrid) {
        this.topGrid.appendChild(card);
      } else if (this.bottomGrid) {
        this.bottomGrid.appendChild(card);
      }
    });

    if (this.banner) {
      this.banner.hidden = cards.length === 0;
    }
  }

  syncLoadMore(incoming) {
    const incomingButton = incoming.querySelector('[data-aa-load-more]');
    const incomingWrap = incoming.querySelector('[data-aa-load-wrap]');
    const nextUrl = (incomingButton && incomingButton.getAttribute('data-next-url')) || '';

    if (!this.loadWrap) {
      if (incomingWrap && nextUrl && this.feed) {
        this.feed.appendChild(document.importNode(incomingWrap, true));
        this.cacheDom();
        if (this.loadButton) this.loadButton.addEventListener('click', this.onLoadMore);
      }
      return;
    }

    if (nextUrl) {
      if (this.loadButton) {
        this.loadButton.dataset.nextUrl = nextUrl;
        this.loadButton.setAttribute('href', nextUrl);
        this.loadButton.disabled = false;
        this.loadButton.setAttribute('aria-disabled', 'false');
      }
      this.loadWrap.hidden = false;
    } else {
      this.hideLoadMore();
    }
  }

  hideLoadMore() {
    if (this.loadWrap) this.loadWrap.hidden = true;
    if (this.loadButton) {
      this.loadButton.dataset.nextUrl = '';
      this.loadButton.removeAttribute('href');
      this.loadButton.disabled = true;
      this.loadButton.setAttribute('aria-disabled', 'true');
    }
  }

  updateLoadMore(nextUrl) {
    if (!this.loadWrap || !this.loadButton) return;
    if (nextUrl) {
      this.loadButton.dataset.nextUrl = nextUrl;
      this.loadButton.setAttribute('href', nextUrl);
      this.loadButton.disabled = false;
      this.loadButton.setAttribute('aria-disabled', 'false');
      this.loadWrap.hidden = false;
    } else {
      this.hideLoadMore();
    }
  }

  visibleCards() {
    return this.querySelectorAll('[data-aa-card]');
  }

  updateCount(visible, totalFromServer) {
    if (!this.countEl) return;
    let count;
    if (this.mode === 'search') {
      count = this.activeTag ? visible : Number(totalFromServer || visible);
    } else {
      count = Number(this.dataset.totalCount || totalFromServer || visible);
    }
    const one = this.dataset.countOne || '{{ count }} article';
    const other = this.dataset.countOther || '{{ count }} articles';
    const template = count === 1 ? one : other;
    this.countEl.textContent = template.replace('{{ count }}', String(count));
  }

  toggleEmpty() {
    const hasCards = this.visibleCards().length > 0;
    if (this.empty) this.empty.hidden = hasCards;
    if (this.topGrid) this.topGrid.hidden = !hasCards && !this.topGrid.querySelector('[data-aa-card]');
    if (this.banner && this.mode === 'search') {
      this.banner.hidden = !hasCards;
    }
  }
}

if (!customElements.get('all-articles')) {
  customElements.define('all-articles', AllArticles);
}
